import { NotFoundError } from '@magicdoorinc/errors';
import type { SubmitRejection } from '~/use-cases/types/SubmitRejection';

export class RentalApplicationNotFoundError extends NotFoundError {
  constructor(message?: string) {
    super(404, message);
    Object.setPrototypeOf(this, RentalApplicationNotFoundError.prototype);
  }
}

export class CannotPerformScreeningError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, CannotPerformScreeningError.prototype);
  }
}

export class CannotLoadPdfFileError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, CannotLoadPdfFileError.prototype);
  }
}

export class CannotMakePaymentsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, CannotMakePaymentsError.prototype);
  }
}

export class BusinessInfoNotFoundError extends Error {
  constructor() {
    super('Business info not found');
    Object.setPrototypeOf(this, BusinessInfoNotFoundError.prototype);
  }
}

export class ValidationError extends Error {
  constructor(public message: string) {
    super(message);
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class PaymentDeclinedError extends ValidationError {
  constructor(
    public readonly failureCode?: string,
    public readonly failureMessage?: string
  ) {
    super('payment_declined');
    Object.setPrototypeOf(this, PaymentDeclinedError.prototype);
  }
}

export class ApplicationNotQualifiedError extends Error {
  constructor(public readonly errors: readonly SubmitRejection[]) {
    super(errors.join(', ') || 'application_not_qualified');
    Object.setPrototypeOf(this, ApplicationNotQualifiedError.prototype);
    this.name = 'ApplicationNotQualifiedError';
  }
}

export class FileUploadFailedError extends Error {
  constructor(public readonly statusCode: number) {
    super(`file_upload_failed:${statusCode}`);
    Object.setPrototypeOf(this, FileUploadFailedError.prototype);
    this.name = 'FileUploadFailedError';
  }
}

export class CoApplicantInviteFailedError extends Error {
  constructor(cause: unknown) {
    super('co_applicant_invite_failed', { cause });
    Object.setPrototypeOf(this, CoApplicantInviteFailedError.prototype);
    this.name = 'CoApplicantInviteFailedError';
  }
}

export class FileNotAcceptedError extends Error {
  constructor(public readonly fileName: string) {
    super('file_not_accepted');
    Object.setPrototypeOf(this, FileNotAcceptedError.prototype);
    this.name = 'FileNotAcceptedError';
  }
}

export class StripeUnavailableError extends Error {
  constructor() {
    super('Stripe could not be loaded');
    Object.setPrototypeOf(this, StripeUnavailableError.prototype);
  }
}

export class RentalApplicationPaymentFailedError extends Error {
  constructor() {
    super('The rental application payment failed');
    Object.setPrototypeOf(this, RentalApplicationPaymentFailedError.prototype);
  }
}

export class GoogleMapsUnavailableError extends Error {
  constructor() {
    super('google_maps_unavailable');
    Object.setPrototypeOf(this, GoogleMapsUnavailableError.prototype);
    this.name = 'GoogleMapsUnavailableError';
  }
}

export class CurrentUnitUnlistedError extends Error {
  constructor() {
    super('current_unit_unlisted');
    Object.setPrototypeOf(this, CurrentUnitUnlistedError.prototype);
    this.name = 'CurrentUnitUnlistedError';
  }
}

export class LeaseInsuranceMissingFieldsError extends ValidationError {
  constructor() {
    super('lease_insurance_missing_fields');
    Object.setPrototypeOf(this, LeaseInsuranceMissingFieldsError.prototype);
  }
}

export class LeaseOrSignatureNotFoundError extends ValidationError {
  constructor() {
    super('lease_or_signature_not_found');
    Object.setPrototypeOf(this, LeaseOrSignatureNotFoundError.prototype);
  }
}

export class LandlordVerificationResponseRequiredError extends ValidationError {
  constructor() {
    super('landlord_verification_response_required');
    Object.setPrototypeOf(this, LandlordVerificationResponseRequiredError.prototype);
  }
}

export class InsuranceLinkNotFoundError extends ValidationError {
  constructor() {
    super('insurance_link_not_found');
    Object.setPrototypeOf(this, InsuranceLinkNotFoundError.prototype);
  }
}

export class FileSessionResponseInvalidError extends Error {
  constructor() {
    super('file_session_response_invalid');
    Object.setPrototypeOf(this, FileSessionResponseInvalidError.prototype);
    this.name = 'FileSessionResponseInvalidError';
  }
}

export class LeaseSigningNoSignersError extends ValidationError {
  constructor() {
    super('lease_signing_no_signers');
    Object.setPrototypeOf(this, LeaseSigningNoSignersError.prototype);
  }
}

export class ProductExperienceAccessExpiredError extends Error {
  constructor() {
    super('product_experience_access_expired');
    Object.setPrototypeOf(this, ProductExperienceAccessExpiredError.prototype);
  }
}

export class CannotRenderPdfPageError extends Error {
  constructor() {
    super('cannot_render_pdf_page');
    Object.setPrototypeOf(this, CannotRenderPdfPageError.prototype);
  }
}
